import { z } from 'zod';
import {
  isCorporateForm,
  MIN_EXECUTIVES,
  shouldShowListedCompanyField,
} from './constants';

/**
 * Creates the Zod validation schema for the Identification Form
 * @param tForms Translation function for error messages
 * @returns Zod schema with translations
 */
export const createIdentificationSchema = (tForms: (key: string) => string) =>
  z
    .object({
      // 1. Contracting Party Details
      companyName: z
        .string()
        .min(1, tForms('identification.validation.companyNameRequired')),
      headOfficeAddress: z
        .string()
        .min(1, tForms('identification.validation.headOfficeAddressRequired')),
      postcode: z
        .string()
        .min(1, tForms('identification.validation.postcodeRequired')),
      city: z.string().min(1, tForms('identification.validation.cityRequired')),
      country: z
        .string()
        .min(1, tForms('identification.validation.countryRequired'))
        .length(2, tForms('identification.validation.countryInvalidFormat')),
      legalForm: z
        .string()
        .min(1, tForms('identification.validation.legalFormRequired')),
      legalFormOther: z.string().max(255).optional(),

      // 1.1 Additional contracting party details
      isFinancialIntermediary: z
        .string()
        .min(
          1,
          tForms('identification.validation.financialIntermediaryRequired')
        ),
      financialIntermediaryType: z.string().optional(),
      financialIntermediaryTypeOther: z.string().optional(),
      supervisoryOrganization: z.string().optional(),
      listedCompany: z.string().optional(),
      stockExchange: z.string().optional(),
      tickerSymbol: z.string().optional(),
      subsidiaryOwnership: z.string().optional(),
      parentCompanyExchange: z.string().optional(),
      parentTicker: z.string().optional(),

      // 1.2 Information on business activities
      employees: z
        .string()
        .min(1, tForms('identification.validation.employeesRequired')),
      shellCompany: z.string().optional(),
      businessDescription: z.string().optional(),

      // 1.3 Operational Office (Optional)
      sameAddressAsContractingParty: z.boolean().optional(),
      operationalOfficeCompanyName: z.string().optional(),
      operationalOfficeAddress: z.string().optional(),
      operationalOfficePostcode: z.string().optional(),
      operationalOfficeCity: z.string().optional(),
      operationalOfficeCountry: z
        .string()
        .length(2, tForms('identification.validation.countryInvalidFormat'))
        .optional()
        .or(z.literal('')),

      // 3 Politically Exposed Persons (PEP)
      isPepInvolved: z.string().optional(),
      politicallyExposedPersons: z
        .array(
          z.object({
            firstName: z
              .string()
              .min(1, tForms('identification.validation.pepFirstNameRequired')),
            lastName: z
              .string()
              .min(1, tForms('identification.validation.pepLastNameRequired')),
            pepReason: z
              .string()
              .min(1, tForms('identification.validation.pepReasonRequired')),
          })
        )
        .optional(),

      // 2. Executive Bodies / Authorized representatives
      executives: z
        .array(
          z.object({
            lastName: z
              .string()
              .min(1, tForms('identification.validation.lastNameRequired')),
            firstName: z
              .string()
              .min(1, tForms('identification.validation.firstNameRequired')),
            address: z
              .string()
              .min(1, tForms('identification.validation.addressRequired')),
            postcode: z
              .string()
              .min(1, tForms('identification.validation.postcodeRequired')),
            city: z
              .string()
              .min(1, tForms('identification.validation.cityRequired')),
            country: z
              .string()
              .min(1, tForms('identification.validation.countryRequired'))
              .length(
                2,
                tForms('identification.validation.countryInvalidFormat')
              ),
            position: z
              .string()
              .min(1, tForms('identification.validation.positionRequired')),
            birthDate: z
              .date({
                message: tForms('identification.validation.birthDateRequired'),
              })
              .optional()
              .refine(date => date !== undefined, {
                message: tForms('identification.validation.birthDateRequired'),
              }),
            nationality: z
              .string()
              .min(1, tForms('identification.validation.nationalityRequired'))
              .length(
                2,
                tForms('identification.validation.nationalityInvalidFormat')
              ),
          })
        )
        .min(
          MIN_EXECUTIVES,
          tForms('identification.validation.executivesMinimum')
        ),

      // Power of attorney provisions
      powerOfAttorneyAttached: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      // Note: Shell Company field validation removed - field is no longer displayed
      // Previously required shellCompany when employees < 6, but this requirement
      // has been deprecated per business requirements update
      // NOPO-87

      // Conditional validation for Legal Form fields
      if (
        data.country === 'CH' &&
        data.legalForm === 'other' &&
        !data.legalFormOther?.trim()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: tForms('identification.validation.legalFormOtherRequired'),
          path: ['legalFormOther'],
        });
      }

      // Conditional validation for Financial Intermediary fields
      if (data.isFinancialIntermediary === 'yes') {
        if (!data.financialIntermediaryType?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.financialIntermediaryTypeRequired'
            ),
            path: ['financialIntermediaryType'],
          });
        }
        // Validate financialIntermediaryTypeOther when 'other' is selected
        if (
          data.financialIntermediaryType === 'other' &&
          !data.financialIntermediaryTypeOther?.trim()
        ) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.financialIntermediaryTypeOtherRequired'
            ),
            path: ['financialIntermediaryTypeOther'],
          });
        }
        if (!data.supervisoryOrganization?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.supervisoryOrganizationRequired'
            ),
            path: ['supervisoryOrganization'],
          });
        }
      }

      // Conditional validation for Listed Company fields
      if (data.listedCompany === 'yes') {
        if (!data.stockExchange?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('identification.validation.stockExchangeRequired'),
            path: ['stockExchange'],
          });
        }
        if (!data.tickerSymbol?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('identification.validation.tickerSymbolRequired'),
            path: ['tickerSymbol'],
          });
        }
      }

      // Conditional validation for Parent/Subsidiary Company fields
      if (data.subsidiaryOwnership === 'yes') {
        if (!data.parentCompanyExchange?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.parentCompanyExchangeRequired'
            ),
            path: ['parentCompanyExchange'],
          });
        }
        if (!data.parentTicker?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('identification.validation.parentTickerRequired'),
            path: ['parentTicker'],
          });
        }
      }

      // subsidiaryOwnership is only visible on corporate legal forms
      if (
        isCorporateForm(data.legalForm) &&
        !data.subsidiaryOwnership?.trim()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: tForms(
            'identification.validation.subsidiaryOwnershipRequired'
          ),
          path: ['subsidiaryOwnership'],
        });
      }

      // Conditional validation for PEP fields
      if (data.isPepInvolved === 'yes') {
        if (
          !data.politicallyExposedPersons ||
          data.politicallyExposedPersons.length === 0
        ) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('identification.validation.pepMinimum'),
            path: ['politicallyExposedPersons'],
          });
        }
      }

      // Conditional validation for Legal Form dependent fields
      // listedCompany is required for all corporate forms except GmbH
      if (shouldShowListedCompanyField(data.legalForm)) {
        if (!data.listedCompany?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('identification.validation.listedCompanyRequired'),
            path: ['listedCompany'],
          });
        }
      }

      // Conditional validation for Operational Office fields (1.3)
      // These fields are required when sameAddressAsContractingParty is false/unchecked
      if (!data.sameAddressAsContractingParty) {
        if (!data.operationalOfficeCompanyName?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.operationalOfficeCompanyNameRequired'
            ),
            path: ['operationalOfficeCompanyName'],
          });
        }
        if (!data.operationalOfficeAddress?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.operationalOfficeAddressRequired'
            ),
            path: ['operationalOfficeAddress'],
          });
        }
        if (!data.operationalOfficePostcode?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.operationalOfficePostcodeRequired'
            ),
            path: ['operationalOfficePostcode'],
          });
        }
        if (!data.operationalOfficeCity?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.operationalOfficeCityRequired'
            ),
            path: ['operationalOfficeCity'],
          });
        }
        if (!data.operationalOfficeCountry?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'identification.validation.operationalOfficeCountryRequired'
            ),
            path: ['operationalOfficeCountry'],
          });
        }
      }
    });
